// --- Dependencies -------------------------------------------------------------------------------
import { NavLink, useLocation } from "react-router-dom";
// --- Our Modules --------------------------------------------------------------------------------
import constants from "@/config/constants";
import useNavTabs from "@/hooks/useNavTabs";
import useTranslation from "@/hooks/useTranslation";
import { isSectionReentry } from "@/utils";
import Icon from "@/UI/atoms/Icon";
import "./styles.css";
// --- Logic --------------------------------------------------------------------------------------
type Props = { onClose: () => void };

/**
 * Everything the bottom bar could not fit — and everything it could, so a thumb reaching for "more"
 * never has to remember which four made the cut. That makes it the phone's whole sidebar.
 *
 * It is a sheet, not a card: edge to edge, with its own close button, because the backdrop it would
 * otherwise be dismissed by is entirely covered.
 */
export default function MoreTabsPopup({ onClose }: Props) {
    const t = useTranslation();
    // The footer group travels with the rest here: on a phone there is no bottom of a sidebar to pin
    // it to, and leaving it out would make the admins tab reachable only by typing its path.
    const { tabs, footerTabs } = useNavTabs();
    const { pathname } = useLocation();

    return (
        <div className="more-tabs">
            <header className="more-tabs__header">
                <div className="more-tabs__brand">
                    <img className="more-tabs__logo" src={constants.logo.round} alt="" />
                    <div className="more-tabs__brand-text">
                        <span className="more-tabs__brand-name">{t.sideBar.brand}</span>
                        <span className="more-tabs__brand-kind">{t.sideBar.kind}</span>
                    </div>
                </div>

                <button type="button" className="more-tabs__close" onClick={onClose} aria-label={t.common.close}>
                    <Icon name="close" size={16} />
                </button>
            </header>

            <nav className="more-tabs__nav">
                {[...tabs, ...footerTabs].map((tab) => (
                    <NavLink
                        key={tab.path}
                        to={tab.path}
                        end={tab.path === "/"}
                        replace={isSectionReentry(tab.path, pathname)}
                        onClick={onClose}
                        className={({ isActive }) => `more-tabs__tab${isActive ? " more-tabs__tab--active" : ""}`}
                    >
                        <Icon name={tab.icon} size={22} />
                        <span className="more-tabs__label">{tab.label}</span>
                    </NavLink>
                ))}
            </nav>

            {/* The sheet covers the backdrop, so tapping outside is not an option. This sits where the
                button that opened it was — the same thumb closes it without moving. */}
            <footer className="more-tabs__footer">
                <button type="button" className="more-tabs__dismiss" onClick={onClose}>
                    <Icon name="close" size={14} />
                    <span>{t.common.close}</span>
                </button>
            </footer>
        </div>
    );
}
